import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompanyName } from "@/lib/company";
import { useFlags } from "@/lib/flags";
import { investigatePath } from "@/components/InvestigateTimeline";
import { cn } from "@/lib/utils";
import { ENGINE_LABELS } from "@/config";
import { Page, PageHeader, Card, StatusBadge, SectionTitle, Bar, Label11, PrivacyNote, Loading, usd, int, plural, pct, ago, shortDate } from "@/components/common";
import { TextScramble } from "@/components/Motion";
import { YourTasks } from "@/components/HiringTerms";
import { EngineWithLogo } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { KIND_LABELS, kindOf, isDocuments } from "@/lib/workKinds";
import { CirclePlus, ChevronRight, Target, Lock, ScanSearch, FileText, SquareTerminal } from "lucide-react";

// design_handoff_crewbench/screens/My Sessions.dc.html: the independent professional's open sessions as cards, the
// ended ones in a table, and a strip with their own budgets (GET /me/budget: today and this month, against their
// limits) and the objectives assigned to them. No founder's name anywhere (the owner, 2026-10-04).
const month = () => new Date().toLocaleDateString("en-GB", { month: "long" });
function greeting(name) {
  const h = new Date().getHours();
  const part = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return `${part}${name ? `, ${name.split(" ")[0]}` : ""}.`;
}
const is = (n) => (n === 1 ? "is" : "are");

// The session's own timeline (Investigate, FEATURE_INVESTIGATE): what the founder sees of it. It sits above the card's
// stretched link, so the card stays one link and this one stays separate.
const TIMELINE_LINK = "relative z-10 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm";
function TimelineLink({ s }) {
  return (
    <Link to={investigatePath("coder", s.id)} className={TIMELINE_LINK} data-testid={`session-timeline-${s.short_id}`}
      title="What your founder sees of this session">
      <ScanSearch className="w-3.5 h-3.5" />Timeline
    </Link>
  );
}
// the card's main link covers the whole card (its ::after); the focus ring is drawn on that cover
const STRETCHED = "after:absolute after:inset-0 after:rounded-[10px] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-ring";

function SessionCard({ s, title }) {
  const review = s.review_ready;
  const timeline = useFlags().investigate;
  return (
    <div className="relative bg-card rounded-[10px] p-4 flex flex-col gap-3 hover:outline hover:outline-2 hover:-outline-offset-2 hover:outline-foreground">
      <div className="flex justify-between items-center gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold tabular-nums"><TextScramble>{s.short_id}</TextScramble></span>
        {review ? <StatusBadge label="In review" tone="warning" /> : <StatusBadge status={s.status} live />}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-gr-ink-secondary">{title || "General"}</span>
        <span className="font-mono text-xs text-muted-foreground">{s.branch}</span>
      </div>
      <EngineWithLogo engine={s.engine} className="text-sm [&_svg]:w-3.5 [&_svg]:h-3.5" />
      <div className="flex flex-col gap-1.5 text-sm tabular-nums">
        <Bar pct={pct(s.spent_usd, s.budget_cap_usd)} full={s.budget_cap_usd > 0 && s.spent_usd >= s.budget_cap_usd} />
        <div className="flex justify-between gap-2 text-muted-foreground">
          <span>{usd(s.spent_usd)} of {usd(s.budget_cap_usd)}</span>
          <span>{s.status === "locked" ? `Locked ${ago(s.locked_at || s.last_activity_at).toLowerCase()}` : `Active ${ago(s.last_activity_at).toLowerCase()}`}</span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <Link to={`/team/session/${s.id}`} data-testid={`session-card-${s.short_id}`} className={cn("flex items-center gap-1 font-semibold", STRETCHED)}>
          {review ? "Read the change report" : "Open"}<ChevronRight className="w-4 h-4" />
        </Link>
        {timeline && <TimelineLink s={s} />}
      </div>
    </div>
  );
}

// what their founder hired them for, and the view their sessions open with (lib/workKinds.js)
function KindCell({ kind }) {
  const docs = isDocuments(kind);
  const Icon = docs ? FileText : SquareTerminal;
  return (
    <div className="flex-[1_1_220px] bg-card px-[18px] py-3.5 flex flex-col gap-1.5" data-testid="my-kind">
      <Label11>Your kind of work</Label11>
      <span className="flex items-center gap-1.5 font-semibold"><Icon className="w-3.5 h-3.5 shrink-0" />{KIND_LABELS[kind]}</span>
      <span className="text-sm text-muted-foreground [text-wrap:pretty]">
        {docs ? "Your sessions open the documents view: you tell the agent what to change." : "Your sessions open the terminal."} Your founder sets it.
      </span>
    </div>
  );
}

// one of their budgets: what they've used against its limit, or what they've spent when no limit is set
function BudgetCell({ label, b, line, none }) {
  const capped = b && b.cap_usd != null;
  const p = capped ? pct(b.spent_usd, b.cap_usd) : 0;
  const full = capped && b.spent_usd >= b.cap_usd;
  return (
    <div className="flex-[1_1_220px] bg-card px-[18px] py-3.5 flex flex-col gap-2">
      <div className="flex justify-between gap-2"><Label11>{label}</Label11>{capped && <span className={full ? "text-sm text-gr-danger" : "text-sm"}>{p}%</span>}</div>
      {capped && <Bar pct={p} full={full} />}
      <span className={full ? "text-sm text-gr-danger" : "text-sm text-muted-foreground"}>
        {!b ? "—" : capped ? `${usd(b.spent_usd)} of ${usd(b.cap_usd)}${line}${full ? " Budget reached." : ""}` : `${usd(b.spent_usd)}${none}`}
      </span>
    </div>
  );
}

export default function Sessions() {
  const { user } = useAuth();
  const timeline = useFlags().investigate;
  const eyebrow = [useCompanyName(), "Your sessions"].filter(Boolean).join(" · ");
  const { data, isLoading } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data, refetchInterval: 5000 });
  const { data: objs } = useQuery({ queryKey: ["objectives"], queryFn: async () => (await api.get("/objectives")).data });
  const { data: budget } = useQuery({ queryKey: ["my-budget"], refetchInterval: 15000, queryFn: async () => (await api.get("/me/budget")).data });
  const { data: mine } = useQuery({ queryKey: ["my-tasks"], refetchInterval: 15000, queryFn: async () => (await api.get("/me/tasks")).data });
  const titles = Object.fromEntries((objs?.objectives || []).map((o) => [o.id, o.title]));
  const action = <Button asChild><Link to="/team/new" data-testid="new-session-nav-btn"><CirclePlus />New session</Link></Button>;
  if (isLoading || !data) return <Page><PageHeader eyebrow={eyebrow} title={greeting(user?.name)} actions={action} /><Loading /></Page>;

  const sessions = data.sessions;
  const open = sessions.filter((s) => s.status !== "ended");
  const ended = sessions.filter((s) => s.status === "ended");
  const running = open.filter((s) => s.status === "active").length;
  const review = open.filter((s) => s.review_ready).length;
  const status = [running ? `${plural(running, "session")} ${is(running)} running.` : "No session is running.",
                  review ? `${int(review)} ${is(review)} waiting for review.` : ""].filter(Boolean).join(" ");
  // objectives they were put on before hiring terms existed (the others are in Your tasks, with their terms)
  const withTerms = new Set((mine?.tasks || []).map((t) => t.objective_id));
  const assigned = (objs?.objectives || []).filter((o) => o.status !== "done" && (o.assignees || []).includes(user?.id) && !withTerms.has(o.id));
  const grid = timeline ? "min-w-[720px] grid-cols-[72px_minmax(0,1.4fr)_170px_96px_100px_80px]" : "min-w-[640px] grid-cols-[72px_minmax(0,1.4fr)_170px_96px_100px]";
  return (
    <Page data-testid="coder-sessions">
      <PageHeader eyebrow={eyebrow} title={greeting(user?.name)} status={status} actions={action} />
      <div className="flex flex-wrap gap-px bg-gr-hairline rounded-[10px] overflow-hidden tabular-nums">
        <KindCell kind={kindOf(user)} />
        <BudgetCell label="Your budget today" b={budget?.daily} line=". Resets each day." none=" today. No daily limit set." />
        <BudgetCell label={`Your budget in ${month()}`} b={budget?.monthly}
          line={` across ${plural(budget?.sessions_month || 0, "session")}.`}
          none={` across ${plural(budget?.sessions_month || 0, "session")}. No monthly limit set.`} />
        {assigned.length > 0 && (
          <div className="flex-[1_1_220px] bg-card px-[18px] py-3.5 flex flex-col gap-1.5">
            <Label11>Also assigned to you</Label11>
            {assigned.map((o) => <span key={o.id} className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 shrink-0" />{o.title}</span>)}
          </div>
        )}
      </div>

      <YourTasks engagement={user?.engagement === "employee" ? "employee" : "independent"} />

      <section className="flex flex-col gap-3">
        <SectionTitle>Open</SectionTitle>
        {open.length ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
            {open.map((s) => <SessionCard key={s.id} s={s} title={titles[s.objective_id]} />)}
          </div>
        ) : (
          <Card className="px-5 py-6 text-muted-foreground" data-testid="no-sessions">
            {sessions.length ? "No open sessions. Start one to keep working." : "No sessions yet. Start one to begin coding with an AI engine."}
          </Card>
        )}
      </section>

      {ended.length > 0 && (
        <section className="flex flex-col gap-3">
          <SectionTitle>Ended</SectionTitle>
          <div className="bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums">
            {ended.map((s) => (
              <div key={s.id} className={cn("relative grid gap-3.5 items-center px-4 py-[11px] border-b last:border-b-0 hover:bg-gr-hover", grid)}>
                <Link to={`/team/session/${s.id}`} data-testid={`session-card-${s.short_id}`}
                  className="text-base font-semibold after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring">{s.short_id}</Link>
                <span className="text-base truncate">{titles[s.objective_id] || "General"}</span>
                <span className="text-muted-foreground">{ENGINE_LABELS[s.engine] || s.engine}</span>
                <span className="text-muted-foreground">{usd(s.spent_usd)}</span>
                <span className="text-muted-foreground text-right">{shortDate(s.ended_at || s.last_activity_at)}</span>
                {timeline && <span className="flex justify-end"><TimelineLink s={s} /></span>}
              </div>
            ))}
          </div>
        </section>
      )}

      <PrivacyNote icon={Lock}>
        Your prompts and the agent's replies are encrypted, and only you can read them, only while a session is unlocked. Founders see files, lines, commits and spend.
      </PrivacyNote>
    </Page>
  );
}
